import { useEffect, useRef, useState } from "react";
import api, { fmtDateTime, formatApiError, eur } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Handshake, FileText, Upload, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { logoSrc } from "@/components/BrandLogo";
import { toast } from "sonner";

const STATUS = { pending: "bg-amber-50 text-amber-700", quoted: "bg-sky-50 text-sky-700", accepted: "bg-emerald-50 text-emerald-700", declined: "bg-rose-50 text-rose-700", cancelled: "bg-zinc-100 text-zinc-500" };

export default function RequestServices() {
  const [catalog, setCatalog] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reqFor, setReqFor] = useState(null);
  const load = () => {
    api.get("/service-catalog").then((r) => setCatalog(r.data));
    api.get("/service-requests").then((r) => setRequests(r.data));
  };
  useEffect(() => { load(); }, []);
  const respond = async (r, status) => { await api.put(`/service-requests/${r.id}/status`, { status }); toast.success(`Quote ${status}`); load(); };

  return (
    <AdminLayout nav={adminNav} title="Request Amden Services">
      <PageHeader title="Amden Services" subtitle="Request professional services from Amden and receive quotes" />

      <div className="mb-8">
        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Available services</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {catalog.map((s) => (
            <div key={s.id} data-testid={`service-${s.id}`} className="bg-white border border-zinc-200 rounded-lg p-4 flex flex-col">
              <div className="h-9 w-9 rounded-md bg-amber-500/10 flex items-center justify-center mb-3"><Handshake className="h-5 w-5 text-amber-600" /></div>
              <div className="font-heading font-bold text-slate-900">{s.name}</div>
              <p className="text-sm text-zinc-500 mt-1 flex-1">{s.description}</p>
              <Button size="sm" className="mt-3 bg-slate-900 text-white" data-testid={`request-service-${s.id}`} onClick={() => setReqFor(s)}>Request quote</Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">My requests</h3>
        <div className="space-y-3">
          {requests.length === 0 && <div className="text-zinc-400 text-sm">No requests yet.</div>}
          {requests.map((r) => (
            <div key={r.id} data-testid={`my-request-${r.id}`} className="bg-white border border-zinc-200 rounded-lg p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading font-bold text-slate-900">{r.service_name}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${STATUS[r.status]}`}>{r.status}</span>
                <span className="text-xs text-zinc-400 ml-auto">{fmtDateTime(r.created_at)}</span>
              </div>
              {r.notes && <p className="text-sm text-zinc-600 mt-2">{r.notes}</p>}
              {r.quote && (
                <div className="mt-3 bg-sky-50 border border-sky-100 rounded-md p-3">
                  <div className="font-bold text-sky-800">Amden quote: {eur(r.quote.amount)}</div>
                  {r.quote.notes && <div className="text-sm text-zinc-600 mt-1">{r.quote.notes}</div>}
                  {r.quote.file_url && <a href={logoSrc(r.quote.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-600 mt-1"><FileText className="h-3.5 w-3.5" />Quote PDF</a>}
                  {r.quote.link && <a href={r.quote.link} target="_blank" rel="noreferrer" className="block text-xs text-sky-600 mt-1 truncate">{r.quote.link}</a>}
                  {r.status === "quoted" && (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="h-8 bg-emerald-600 text-white" data-testid={`accept-${r.id}`} onClick={() => respond(r, "accepted")}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Accept</Button>
                      <Button size="sm" variant="outline" className="h-8" data-testid={`decline-${r.id}`} onClick={() => respond(r, "declined")}><XCircle className="h-3.5 w-3.5 mr-1" />Decline</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {reqFor && <RequestDialog svc={reqFor} onClose={() => setReqFor(null)} onDone={() => { setReqFor(null); load(); }} />}
    </AdminLayout>
  );
}

function RequestDialog({ svc, onClose, onDone }) {
  const ref = useRef(null);
  const [notes, setNotes] = useState("");
  const [fileUrl, setFileUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const res = await api.post("/service-requests/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setFileUrl(res.data.file_url); setFileName(res.data.filename); toast.success("Attached"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    try { await api.post("/service-requests", { service_id: svc.id, service_name: svc.name, notes, attachment_url: fileUrl }); toast.success("Request sent to Amden"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Request: {svc.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-zinc-500">{svc.description}</p>
          <div><Label>Notes for Amden</Label><Textarea data-testid="req-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Tell us what you need…" className="mt-1" /></div>
          <div>
            <Label>Attachment (optional)</Label>
            <input ref={ref} type="file" className="hidden" data-testid="req-file-input" onChange={(e) => uploadFile(e.target.files?.[0])} />
            <Button variant="outline" size="sm" className="mt-1" data-testid="req-upload" disabled={busy} onClick={() => ref.current?.click()}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1" />{fileName || "Attach file"}</>}</Button>
          </div>
          <Button data-testid="req-submit" onClick={submit} className="w-full bg-slate-900 text-white">Send request</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
